import { Stack, Text, Checkbox, TextInput, Group, Button, Slider, Box, SimpleGrid, Chip } from '@mantine/core';
import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { FoodAutocompleteInput } from '../UI/FoodAutocompleteInput';
import { searchIngredients } from '../../data/ingredientDatabase';

type CuisinePreferenceProps = {
  value: { cuisinePreferences: string[]; avoidCuisines: string[]; dislikes: string[]; cuisineAdventure?: number };
  onChange: (value: { cuisinePreferences: string[]; avoidCuisines: string[]; dislikes: string[]; cuisineAdventure?: number }) => void;
};

export type CuisinePreferenceRef = {
  flushInputs: () => void;
};

const CUISINE_TYPES = [
  { name: 'Italian', flag: '🇮🇹' },
  { name: 'Chinese', flag: '🇨🇳' },
  { name: 'Japanese', flag: '🇯🇵' },
  { name: 'Indian', flag: '🇮🇳' },
  { name: 'Mexican', flag: '🇲🇽' },
  { name: 'French', flag: '🇫🇷' },
  { name: 'Thai', flag: '🇹🇭' },
  { name: 'American', flag: '🇺🇸' },
  { name: 'Korean', flag: '🇰🇷' },
  { name: 'Vietnamese', flag: '🇻🇳' },
  { name: 'Mediterranean', flag: '🏛️' },
  { name: 'Middle Eastern', flag: '🏺' },
  { name: 'Latin', flag: '🌎' },
  { name: 'African', flag: '🌍' },
];

const CuisinePreferenceComponent = forwardRef<CuisinePreferenceRef, CuisinePreferenceProps>(
  ({ value, onChange }, ref) => {
    const [otherCuisineInput, setOtherCuisineInput] = useState('');
    const [avoidCuisineInput, setAvoidCuisineInput] = useState('');
    const [dislikeInput, setDislikeInput] = useState('');

    useImperativeHandle(ref, () => ({
      flushInputs: () => {
        const predefinedNames = CUISINE_TYPES.map(c => c.name);
        let newValue = { ...value };
        let updated = false;

        // Flush other cuisines input
        if (otherCuisineInput.trim()) {
          const newCuisines = otherCuisineInput
            .split(',')
            .map(item => item.trim())
            .filter(item => item.length > 0)
            .map(item => item.charAt(0).toUpperCase() + item.slice(1).toLowerCase())
            .filter(item => !newValue.cuisinePreferences.some(c => c.toLowerCase() === item.toLowerCase()) && !predefinedNames.some(p => p.toLowerCase() === item.toLowerCase()));

          const predefinedToAdd = otherCuisineInput
            .split(',')
            .map(item => item.trim())
            .filter(item => item.length > 0)
            .map(item => predefinedNames.find(p => p.toLowerCase() === item.toLowerCase()))
            .filter((cuisine): cuisine is string => Boolean(cuisine))
            .filter(cuisine => !newValue.cuisinePreferences.includes(cuisine));

          const allNewCuisines = [...predefinedToAdd, ...newCuisines];
          if (allNewCuisines.length > 0) {
            newValue.cuisinePreferences = [...newValue.cuisinePreferences, ...allNewCuisines];
            newValue.avoidCuisines = (newValue.avoidCuisines || []).filter(avoid =>
              !allNewCuisines.some(newCuisine => newCuisine.toLowerCase() === avoid.toLowerCase())
            );
            updated = true;
            setOtherCuisineInput('');
          }
        }

        // Flush avoid cuisines input
        if (avoidCuisineInput.trim()) {
          const newAvoidCuisines = avoidCuisineInput
            .split(',')
            .map(item => item.trim())
            .filter(item => item.length > 0)
            .map(item => item.charAt(0).toUpperCase() + item.slice(1).toLowerCase())
            .filter(item => !(newValue.avoidCuisines || []).some(c => c.toLowerCase() === item.toLowerCase()) && !predefinedNames.some(p => p.toLowerCase() === item.toLowerCase()));

          const predefinedToAdd = avoidCuisineInput
            .split(',')
            .map(item => item.trim())
            .filter(item => item.length > 0)
            .map(item => predefinedNames.find(p => p.toLowerCase() === item.toLowerCase()))
            .filter((cuisine): cuisine is string => Boolean(cuisine))
            .filter(cuisine => !(newValue.avoidCuisines || []).includes(cuisine));

          const allNewAvoidCuisines = [...predefinedToAdd, ...newAvoidCuisines];
          if (allNewAvoidCuisines.length > 0) {
            newValue.avoidCuisines = [...(newValue.avoidCuisines || []), ...allNewAvoidCuisines];
            newValue.cuisinePreferences = newValue.cuisinePreferences.filter(favorite =>
              !allNewAvoidCuisines.some(newAvoid => newAvoid.toLowerCase() === favorite.toLowerCase())
            );
            updated = true;
            setAvoidCuisineInput('');
          }
        }

        // Flush dislikes input
        if (dislikeInput.trim()) {
          const newDislikes = dislikeInput
            .split(',')
            .map(item => item.trim())
            .filter(item => item.length > 0)
            .map(item => item.charAt(0).toUpperCase() + item.slice(1).toLowerCase())
            .filter(item => !newValue.dislikes.some(d => d.toLowerCase() === item.toLowerCase()));

          if (newDislikes.length > 0) {
            newValue.dislikes = [...newValue.dislikes, ...newDislikes];
            updated = true;
            setDislikeInput('');
          }
        }

        if (updated) {
          onChange(newValue);
        }
      }
    }));

    // Initialize default cuisineAdventure value when cuisines are selected
    useEffect(() => {
      if (value.cuisinePreferences.length > 0 && value.cuisineAdventure === undefined) {
        onChange({ ...value, cuisineAdventure: 50 });
      }
    }, [value.cuisinePreferences.length, value.cuisineAdventure, onChange, value]);

  const toggleFavoriteCuisine = (cuisine: string) => {
    const currentFavorites = value.cuisinePreferences;
    const currentAvoids = value.avoidCuisines || [];
    
    const updatedFavorites = currentFavorites.includes(cuisine)
      ? currentFavorites.filter(c => c !== cuisine)
      : [...currentFavorites, cuisine];
    
    // Remove from avoid list if adding to favorites (case-insensitive for all cuisines)
    const updatedAvoids = updatedFavorites.includes(cuisine)
      ? currentAvoids.filter(c => c.toLowerCase() !== cuisine.toLowerCase())
      : currentAvoids;
    
    onChange({ ...value, cuisinePreferences: updatedFavorites, avoidCuisines: updatedAvoids });
  };

  const toggleAvoidCuisine = (cuisine: string) => {
    const currentAvoids = value.avoidCuisines || [];
    const currentFavorites = value.cuisinePreferences;
    
    const updatedAvoids = currentAvoids.includes(cuisine)
      ? currentAvoids.filter(c => c !== cuisine)
      : [...currentAvoids, cuisine];
    
    // Remove from favorites list if adding to avoid (case-insensitive for all cuisines)
    const updatedFavorites = updatedAvoids.includes(cuisine)
      ? currentFavorites.filter(c => c.toLowerCase() !== cuisine.toLowerCase())
      : currentFavorites;
    
    onChange({ ...value, avoidCuisines: updatedAvoids, cuisinePreferences: updatedFavorites });
  };

  const addCustomFavoriteCuisine = () => {
    if (!otherCuisineInput.trim()) return;
    
    const predefinedNames = CUISINE_TYPES.map(c => c.name);
    const currentAvoids = value.avoidCuisines || [];
    const currentFavorites = value.cuisinePreferences;
    
    const inputCuisines = otherCuisineInput
      .split(',')
      .map(item => item.trim())
      .filter(item => item.length > 0)
      .map(item => item.charAt(0).toUpperCase() + item.slice(1).toLowerCase());
    
    let updatedFavorites = [...currentFavorites];
    let updatedAvoids = [...currentAvoids];
    
    inputCuisines.forEach(inputCuisine => {
      // Check if it's a predefined cuisine (case-insensitive)
      const matchingPredefined = predefinedNames.find(p => p.toLowerCase() === inputCuisine.toLowerCase());
      
      if (matchingPredefined) {
        // Add predefined cuisine to favorites if not already there
        if (!updatedFavorites.includes(matchingPredefined)) {
          updatedFavorites.push(matchingPredefined);
        }
        // Remove from avoid list if it's there
        updatedAvoids = updatedAvoids.filter(avoid => avoid.toLowerCase() !== matchingPredefined.toLowerCase());
      } else {
        // It's a custom cuisine - add if not already in favorites
        if (!updatedFavorites.some(c => c.toLowerCase() === inputCuisine.toLowerCase())) {
          updatedFavorites.push(inputCuisine);
        }
        // Remove from avoid list if it's there
        updatedAvoids = updatedAvoids.filter(avoid => avoid.toLowerCase() !== inputCuisine.toLowerCase());
      }
    });
    
    onChange({ 
      ...value, 
      cuisinePreferences: updatedFavorites,
      avoidCuisines: updatedAvoids
    });
    setOtherCuisineInput('');
  };

  const addCustomAvoidCuisine = () => {
    if (!avoidCuisineInput.trim()) return;
    
    const predefinedNames = CUISINE_TYPES.map(c => c.name);
    const currentAvoids = value.avoidCuisines || [];
    const currentFavorites = value.cuisinePreferences;
    
    const inputCuisines = avoidCuisineInput
      .split(',')
      .map(item => item.trim())
      .filter(item => item.length > 0)
      .map(item => item.charAt(0).toUpperCase() + item.slice(1).toLowerCase());
    
    let updatedAvoids = [...currentAvoids];
    let updatedFavorites = [...currentFavorites];
    
    inputCuisines.forEach(inputCuisine => {
      // Check if it's a predefined cuisine (case-insensitive)
      const matchingPredefined = predefinedNames.find(p => p.toLowerCase() === inputCuisine.toLowerCase());
      
      if (matchingPredefined) {
        // Add predefined cuisine to avoid if not already there
        if (!updatedAvoids.includes(matchingPredefined)) {
          updatedAvoids.push(matchingPredefined);
        }
        // Remove from favorites list if it's there
        updatedFavorites = updatedFavorites.filter(favorite => favorite.toLowerCase() !== matchingPredefined.toLowerCase());
      } else {
        // It's a custom cuisine - add if not already in avoid list
        if (!updatedAvoids.some(c => c.toLowerCase() === inputCuisine.toLowerCase())) {
          updatedAvoids.push(inputCuisine);
        }
        // Remove from favorites list if it's there
        updatedFavorites = updatedFavorites.filter(favorite => favorite.toLowerCase() !== inputCuisine.toLowerCase());
      }
    });
    
    onChange({ 
      ...value, 
      avoidCuisines: updatedAvoids,
      cuisinePreferences: updatedFavorites
    });
    setAvoidCuisineInput('');
  };

  const addDislike = (text: string) => {
    if (!text.trim()) return;

    const newDislikes = text
      .split(',')
      .map(item => item.trim())
      .filter(item => item.length > 0)
      .filter(item => !value.dislikes.includes(item));
    
    if (newDislikes.length === 0) return;
    
    onChange({ ...value, dislikes: [...value.dislikes, ...newDislikes] });
    setDislikeInput('');
  };

  const removeDish = (dish: string, type: 'favorite' | 'avoid' | 'dislike') => {
    if (type === 'favorite') {
      onChange({ ...value, cuisinePreferences: value.cuisinePreferences.filter(c => c !== dish) });
    } else if (type === 'avoid') {
      onChange({ ...value, avoidCuisines: (value.avoidCuisines || []).filter(c => c !== dish) });
    } else if (type === 'dislike') {
      onChange({ ...value, dislikes: value.dislikes.filter(d => d !== dish) });
    }
  };

  const getAdventureDescription = (level: number) => {
    if (level === 0) return "We'll only suggest recipes from your selected cuisines";
    if (level === 25) return "We'll primarily use your favorites with occasional new options";
    if (level === 50) return "A good mix of familiar and new flavor combinations";
    if (level === 75) return "We'll explore beyond your selections while respecting your tastes";
    if (level === 100) return "Your selections are a starting point for culinary discovery";
    
    // Fallback for intermediate values
    if (level < 30) return "Stick to the classics your family knows and loves";
    if (level < 70) return "A healthy mix of comfort foods and culinary exploration";
    return "Your selections are a starting point for culinary discovery";
  };

  // Separate predefined and custom cuisines
  const predefinedNames = CUISINE_TYPES.map(c => c.name);
  const predefinedFavorites = value.cuisinePreferences.filter(c => predefinedNames.includes(c));
  const customFavorites = value.cuisinePreferences.filter(c => !predefinedNames.includes(c));
  const predefinedAvoids = (value.avoidCuisines || []).filter(c => predefinedNames.includes(c));
  const customAvoids = (value.avoidCuisines || []).filter(c => !predefinedNames.includes(c));

  // Ingredient-catalog suggestions for the "Foods to avoid" input (same catalog
  // that powers pantry autocomplete). Only search once there are 2+ characters,
  // and hide anything already in the list.
  const dislikeSuggestions = dislikeInput.trim().length >= 2
    ? searchIngredients(dislikeInput, 8)
        .map(ing => ing.displayName ?? ing.name)
        .filter(name => !value.dislikes.some(d => d.toLowerCase() === name.toLowerCase()))
    : [];

  return (
    <Stack gap="xl">
      {/* Favorite Cuisines */}
      <Stack gap="md">
        <Text fw={500}>Favorite cuisines</Text>
        
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
          {CUISINE_TYPES.map((cuisine) => {
            const isSelected = predefinedFavorites.includes(cuisine.name);
            const isInAvoidList = predefinedAvoids.includes(cuisine.name);
            return (
              <Checkbox
                key={cuisine.name}
                label={<span>{cuisine.name}&nbsp;&nbsp;{cuisine.flag}</span>}
                checked={isSelected}
                onChange={() => toggleFavoriteCuisine(cuisine.name)}
                size="sm"
                color="green"
                style={{ opacity: isInAvoidList ? 0.5 : 1 }}
                styles={{
                  root: { cursor: 'pointer' },
                  input: { cursor: 'pointer' },
                  label: { cursor: 'pointer' }
                }}
              />
            );
          })}
        </SimpleGrid>

        {/* Add custom favorite cuisine */}
        <Group gap="xs">
          <TextInput
            placeholder="e.g. Caribbean, Moroccan, Peruvian..."
            value={otherCuisineInput}
            onChange={(e) => setOtherCuisineInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && addCustomFavoriteCuisine()}
            style={{ flex: 1 }}
            size="sm"
          />
          <Button size="sm" onClick={addCustomFavoriteCuisine} disabled={!otherCuisineInput.trim()} color="green">
            Add
          </Button>
        </Group>

        {/* Display custom favorite cuisines */}
        {customFavorites.length > 0 && (
          <Group gap="xs" wrap="wrap" className="chip-cloud">
            {customFavorites.map(cuisine => (
              <Chip
                key={cuisine}
                checked={true}
                onChange={() => removeDish(cuisine, 'favorite')}
                variant="filled"
                color="green"
                size="sm"
                style={{ minWidth: 'fit-content' }}
              >
                {cuisine}
              </Chip>
            ))}
          </Group>
        )}
      </Stack>

      {/* Cuisines to Avoid */}
      <Stack gap="md">
        <Text fw={500}>Cuisines to avoid</Text>
        
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
          {CUISINE_TYPES.map((cuisine) => {
            const isSelected = predefinedAvoids.includes(cuisine.name);
            const isInFavoriteList = predefinedFavorites.includes(cuisine.name);
            return (
              <Checkbox
                key={cuisine.name}
                label={<span>{cuisine.name}&nbsp;&nbsp;{cuisine.flag}</span>}
                checked={isSelected}
                onChange={() => toggleAvoidCuisine(cuisine.name)}
                size="sm"
                color="red"
                style={{ opacity: isInFavoriteList ? 0.5 : 1 }}
                styles={{
                  root: { cursor: 'pointer' },
                  input: { cursor: 'pointer' },
                  label: { cursor: 'pointer' }
                }}
              />
            );
          })}
        </SimpleGrid>

        {/* Add custom avoid cuisine */}
        <Group gap="xs">
          <TextInput
            placeholder="e.g. Caribbean, Moroccan, Peruvian..."
            value={avoidCuisineInput}
            onChange={(e) => setAvoidCuisineInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && addCustomAvoidCuisine()}
            style={{ flex: 1 }}
            size="sm"
          />
          <Button size="sm" onClick={addCustomAvoidCuisine} disabled={!avoidCuisineInput.trim()} color="red">
            Add
          </Button>
        </Group>

        {/* Display custom avoid cuisines */}
        {customAvoids.length > 0 && (
          <Group gap="xs" wrap="wrap" className="chip-cloud">
            {customAvoids.map(cuisine => (
              <Chip
                key={cuisine}
                checked={true}
                onChange={() => removeDish(cuisine, 'avoid')}
                variant="filled"
                color="red"
                size="sm"
                style={{ minWidth: 'fit-content' }}
              >
                {cuisine}
              </Chip>
            ))}
          </Group>
        )}
      </Stack>

      {/* Adventure Level - Only show if user has selected cuisines */}
      {value.cuisinePreferences.length > 0 && (
        <Stack gap="md">
          <Text fw={500}>Cuisine adventure level</Text>
          
          <Box px="lg">
            {/* Mobile Slider */}
            <Slider
              value={value.cuisineAdventure ?? 50}
              onChange={(val) => onChange({ ...value, cuisineAdventure: val })}
              min={0}
              max={100}
              step={25}
              size="lg"
              color="orange"
              label={null}
              hiddenFrom="sm"
              marks={[
                { value: 0, label: 'Safe' },
                { value: 50, label: 'Balanced' },
                { value: 100, label: 'Bold' },
              ]}
              styles={{
                thumb: { 
                  border: '2px solid white',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                },
                mark: {
                  fontSize: '11px'
                }
              }}
              my="xl"
            />
            
            {/* Desktop Slider */}
            <Slider
              value={value.cuisineAdventure ?? 50}
              onChange={(val) => onChange({ ...value, cuisineAdventure: val })}
              min={0}
              max={100}
              step={25}
              size="lg"
              color="orange"
              label={null}
              visibleFrom="sm"
              marks={[
                { value: 0, label: 'Safe' },
                { value: 25, label: 'Familiar' },
                { value: 50, label: 'Balanced' },
                { value: 75, label: 'Adventurous' },
                { value: 100, label: 'Bold' },
              ]}
              styles={{
                thumb: { 
                  border: '2px solid white',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                },
                mark: {
                  fontSize: '12px'
                }
              }}
              my="xl"
            />
          </Box>
          
          <Text size="sm" c="dimmed" ta="center" style={{ fontStyle: 'italic' }}>
            {getAdventureDescription(value.cuisineAdventure !== undefined ? value.cuisineAdventure : 50)}
          </Text>
        </Stack>
      )}

      {/* Food Dislikes */}
      <Stack gap="md">
        <Text fw={500}>Foods to avoid</Text>

        <FoodAutocompleteInput
          value={dislikeInput}
          onChange={setDislikeInput}
          onAdd={addDislike}
          suggestions={dislikeSuggestions}
          placeholder="e.g. mushrooms, spicy food, seafood..."
          color="red"
        />

        {value.dislikes.length > 0 && (
          <Group gap="xs" wrap="wrap" className="chip-cloud">
            {value.dislikes.map(dislike => (
              <Chip
                key={dislike}
                checked={true}
                onChange={() => removeDish(dislike, 'dislike')}
                variant="filled"
                color="red"
                size="sm"
                style={{ minWidth: 'fit-content' }}
              >
                {dislike}
              </Chip>
            ))}
          </Group>
        )}
      </Stack>
    </Stack>
  );
});

CuisinePreferenceComponent.displayName = 'CuisinePreference';

export const CuisinePreference = CuisinePreferenceComponent;
